import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Award, Users, Clock } from 'lucide-react';

const Hero = () => {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0D7FA3] via-[#1a5f7a] to-[#C64DAD] py-20 md:py-32">
      {/* Animated gradient mesh background */}
      <div className="absolute inset-0 opacity-20">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-yellow-300 to-transparent rounded-full blur-3xl" />

        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [0, -90, 0]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-pink-400 to-transparent rounded-full blur-3xl" />

      </div>

      {/* Floating geometric shapes */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-40 right-10 w-20 h-20 border-4 border-white/20 rounded-lg" />

      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-40 left-10 w-16 h-16 border-4 border-yellow-300/30 rounded-full" />


      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Logo with glow effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring" }}
            className="flex justify-center mb-8">

            <div className="relative">
              <div className="absolute inset-0 bg-yellow-300 rounded-2xl blur-2xl opacity-40 animate-pulse"></div>
              <img
                src="https://static.lumi.new/26/26cb63c4aa4fe41e44624f57ce3867a5.webp"
                alt="EW Pass Logo"
                className="relative w-28 h-28 md:w-36 md:h-36 rounded-2xl shadow-2xl ring-4 ring-white/20" />

            </div>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center mb-6">

            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-5 py-2.5">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-white text-sm font-semibold">O Netflix do Inglês</span>
            </div>
          </motion.div>

          {/* Headline with gradient text */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-6xl lg:text-7xl font-black text-white text-center mb-6 leading-[1.1]">

            Pare de <span className="line-through decoration-red-400 decoration-4">(re) começar</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">Evolua seu inglês

            </span>
            <br />
            <span className="text-3xl md:text-5xl lg:text-6xl font-bold">
              com estrutura real
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-white/95 text-center mb-10 max-w-3xl mx-auto leading-relaxed font-light">

            Cursos autorais, professores reais, IA 24h no WhatsApp e acompanhamento contínuo.
            <br className="hidden md:block" />
            <span className="font-semibold">Tudo em um só lugar.</span>
          </motion.p>

          {/* Stats mini cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 max-w-4xl mx-auto">

            {[
            { icon: Award, label: '+13 cursos', color: 'yellow' },
            { icon: Clock, label: '500+ horas', color: 'blue' },
            { icon: Users, label: 'Professores reais', color: 'pink' },
            { icon: Sparkles, label: 'Lucy IA 24h', color: 'purple' }].
            map((stat, index) =>
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/15 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300">

                <stat.icon className="w-6 h-6 text-yellow-300 mx-auto mb-2" />
                <p className="text-white text-sm font-semibold">{stat.label}</p>
              </motion.div>
            )}
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="grid md:grid-cols-2 gap-3 mb-12 max-w-3xl mx-auto">

            {[
            'Básico ao avançado em uma plataforma',
            'Aulas ao vivo toda semana',
            'App offline (Android e iOS)',
            'Suporte real quando precisar'].
            map((benefit, index) =>
            <motion.div
              key={index}
              whileHover={{ x: 5 }}
              className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/15 transition-all duration-300">

                <div className="w-6 h-6 bg-yellow-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-gray-900" strokeWidth={3} />
                </div>
                <span className="text-white text-base md:text-lg font-medium">{benefit}</span>
              </motion.div>
            )}
          </motion.div>

          {/* CTA with shimmer effect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex justify-center">

            <motion.button
              onClick={scrollToPricing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative bg-gradient-to-r from-yellow-300 to-yellow-400 hover:from-yellow-400 hover:to-yellow-500 text-gray-900 font-bold text-lg md:text-xl px-12 py-5 rounded-full shadow-2xl transition-all duration-300 overflow-hidden group">

              <span className="relative z-10 flex items-center gap-2">
                Começar agora
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}>

                  →
                </motion.span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </motion.button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="text-center text-white/70 text-sm mt-6">

            ✨ Garantia de 6 meses • Cancele quando quiser
          </motion.p>
        </div>
      </div>
    </section>);

};

export default Hero;